import { useEffect, useRef, useState } from "react";
import { RegionGlobeIcon } from "./RegionGlobeIcon";

type Region = "us" | "hk";

export function RegionSwitcher({
  region,
  label,
  labels,
  onChange,
  withDivider = false,
}: {
  region: Region;
  label: string;
  labels: Record<Region, string>;
  onChange: (region: Region) => void;
  withDivider?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative ml-3 max-sm:ml-0">
      {withDivider ? <span className="pointer-events-none absolute -left-[13px] top-2.5 h-5 w-px bg-[#e9e6ed] max-sm:hidden" aria-hidden="true" /> : null}
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={`inline-flex h-10 w-10 items-center justify-center rounded-[10px] bg-transparent transition-colors hover:text-[#0153e4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6825bd]/[.16] ${open ? "text-[#0153e4]" : "text-[#77717d]"}`}
      >
        <RegionGlobeIcon className="h-5 w-5 shrink-0" />
      </button>
      <div
        role="menu"
        aria-label={label}
        className={`absolute right-0 top-[calc(100%+8px)] z-[80] min-w-[148px] overflow-hidden rounded-xl border border-[#ebe8ee] bg-white/95 py-1.5 shadow-[0_14px_36px_rgba(35,28,43,.16)] backdrop-blur-xl transition duration-150 ${open ? "visible translate-y-0 opacity-100" : "invisible pointer-events-none -translate-y-1 opacity-0"}`}
      >
        {(["us", "hk"] as const).map((item) => {
          const selected = region === item;
          return (
            <button
              key={item}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => {
                setOpen(false);
                if (!selected) onChange(item);
              }}
              className={`flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left text-sm transition-colors hover:bg-[#f5f3f7] focus-visible:bg-[#f5f3f7] focus-visible:outline-none ${selected ? "font-semibold text-[#0153e4]" : "font-medium text-[#4f4954]"}`}
            >
              <span>{labels[item]}</span>
              {selected ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0153e4]" aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
